/**
 * bandMatrix — the read-only Admin "Rate bands by role" panel client
 * (0134; founder ruling 2026-07-07: which rate bands each role — and the
 * per-staff SPICE flag — may pick becomes VISIBLE in the product).
 *
 *   GET /api/t/{slug}/v1/band-visibility  (manage_team)
 *
 * Served (not FE-mirrored) from the SAME resolver the pickers + the
 * server-side allowed-band guard use (resolveBandVisibility over
 * tenants.config.billing.band_visibility, founder-matrix default) — so
 * the panel can never drift from what the product enforces.
 */

import { ApiError } from './api';

/** One role column of the panel (wire value + owner-facing label). */
export interface BandMatrixRoleColumn {
  role: string;
  label: string;
}

/** One band row: per-role pickability + each flag's marginal grant. */
export interface BandMatrixRow {
  band: string;
  label: string;
  cells: Record<string, boolean>;
  /** true = any staffer with the does_care_management flag may pick this band. */
  care_management: boolean;
  /** true = any staffer with the does_spice flag may pick this band. */
  spice: boolean;
  /** true = switched off (billing.bands_off): nobody is offered it while
   *  it is off, whatever the cells say. Absent from an older answer. */
  off?: boolean;
}

export interface BandMatrixResponse {
  roles: BandMatrixRoleColumn[];
  rows: BandMatrixRow[];
}

export const bandMatrixApi = {
  async get(slug: string, signal?: AbortSignal): Promise<BandMatrixResponse> {
    const path = `/api/t/${encodeURIComponent(slug)}/v1/band-visibility`;
    const res = await fetch(path, { method: 'GET', headers: { Accept: 'application/json' }, signal });
    if (!res.ok) {
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        body = await res.text().catch(() => null);
      }
      throw new ApiError(res.status, body, `GET ${path} → HTTP ${res.status}`);
    }
    return (await res.json()) as BandMatrixResponse;
  },
};
